import { OfferItem, OfferPageDef, OffersData, Rotation } from "../data/offers";
import { DAILY_OFFER_IDS, DAILY_PAGE, dailyThemeFor, pageOf, periodOf } from "./deals";
import { utcDay } from "./orders";

/**
 * The `deals` save section (docs/SHOP.md §4): what was bought in each offer's current rotation period, the picks,
 * prizes and gear slots the player chose, today's Daily Deals theme and its changes, this week's Weekly Deal tokens and
 * claimed milestones, the store transactions already granted, and the purchase intents (what was shown when the
 * player tapped a price, granted when the store's transaction arrives — possibly later: Ask to Buy, a crash).
 */

/** An intent older than this is dropped (a store transaction that never came). */
export const INTENT_TTL_SECONDS = 7 * 86_400;
/** At most this many intents are kept (oldest dropped). */
export const MAX_INTENTS = 20;
/** An unchosen pick, prize or gear slot. */
export const NO_CHOICE = -1;

/** What the player saw when they tapped a price: granted exactly, when the store's transaction for `product` arrives. */
export interface DealIntent {
  readonly product: string;
  readonly offerId: string;
  readonly pageId: string;
  readonly periodKey: number;
  readonly priceCents: number;
  /** The choices as shown (the items are re-resolved from the catalog with them, at the player's level at the grant). */
  readonly theme: string;
  readonly picks: readonly number[];
  readonly prize: number;
  readonly gearSlots: readonly number[];
  readonly at: number;
}

export interface DealsJson {
  bought: Record<string, { key: number; n: number }>;
  picks: Record<string, number[]>;
  prizes: Record<string, number>;
  gearSlots: Record<string, number[]>;
  daily: { day: number; theme: string; changes: number };
  week: { key: number; tokens: number; claimed: number[] };
  granted: string[];
  intents: DealIntent[];
  /** `badgeUntilSeen` pages Deals has been opened on (MP-04; absent before it). */
  seen?: string[];
}

const isInt = (n: unknown): n is number => Number.isInteger(n);
const intList = (v: unknown): number[] => (Array.isArray(v) ? v.filter(isInt) : []);

export class DealsState {
  /** Per offer id (daily.1..3 and daily.all included): purchases in the period `key`. */
  bought: Record<string, { key: number; n: number }> = {};
  picks: Record<string, number[]> = {};
  prizes: Record<string, number> = {};
  gearSlots: Record<string, number[]> = {};
  daily = { day: -1, theme: "", changes: 0 };
  week = { key: -1, tokens: 0, claimed: [] as number[] };
  /** Store transaction ids already granted (grants are once per transaction, ADR 0013). */
  granted: string[] = [];
  intents: DealIntent[] = [];
  /** `badgeUntilSeen` page ids Deals has been opened on since they went on sale (MP-04): their badge is cleared. */
  seen: string[] = [];

  markSeen(pageId: string): void { if (!this.seen.includes(pageId)) this.seen.push(pageId); }

  /** Purchases of `offerId` in the current period of `rotation`. */
  boughtNow(offerId: string, rotation: Rotation, now: number): number {
    const b = this.bought[offerId], key = periodOf(rotation, now).key;
    return b && b.key === key ? b.n : 0;
  }

  /** Purchases left now (Infinity for rotation `none`). */
  remaining(offerId: string, limit: number, rotation: Rotation, now: number): number {
    return rotation === "none" ? Infinity : Math.max(0, limit - this.boughtNow(offerId, rotation, now));
  }

  /** Counts a purchase in the period it was made in (`periodKey` from the intent; an older period's does not count now). */
  noteBought(offerId: string, periodKey: number): void {
    const b = this.bought[offerId];
    this.bought[offerId] = b && b.key === periodKey ? { key: periodKey, n: b.n + 1 } : { key: periodKey, n: 1 };
  }

  setPick(offerId: string, slot: number, choice: number): void { setAt(this.picks, offerId, slot, choice); }
  setPrize(offerId: string, prize: number): void { this.prizes[offerId] = prize; }
  setGearSlot(offerId: string, index: number, slot: number): void { setAt(this.gearSlots, offerId, index, slot); }

  /** Today's theme: the rolled one, or the one Change set today. */
  dailyTheme(data: OffersData, playerId: string, now: number): string {
    const day = utcDay(now);
    if (this.daily.day === day && this.daily.theme !== "") return this.daily.theme;
    return dailyThemeFor(data, playerId, day);
  }

  /** Whether any Daily Deal was bought today (Change and Buy All need none). */
  dailyBoughtToday(now: number): boolean {
    return [...DAILY_OFFER_IDS, "daily.all"].some((id) => this.boughtNow(id, "daily", now) > 0);
  }

  /** Change: swaps today's theme, before any Daily Deal is bought, at most 1 + `extraChanges` (VIP) times a day. */
  changeTheme(data: OffersData, now: number, themeId: string, extraChanges: number): true | string {
    const d = pageOf(data, DAILY_PAGE)?.daily;
    if (!d || !d.themes.some((t) => t.id === themeId)) return "no such theme";
    if (this.dailyBoughtToday(now)) return "already bought today";
    const day = utcDay(now);
    const used = this.daily.day === day ? this.daily.changes : 0;
    if (used >= 1 + extraChanges) return "no changes left today";
    this.daily = { day, theme: themeId, changes: used + 1 };
    return true;
  }

  /** This UTC week's Weekly Deal tokens. */
  tokens(now: number): number { return this.week.key === periodOf("weekly", now).key ? this.week.tokens : 0; }

  /**
   * Adds tokens in the week `weekKey` (the intent's period). A new week starts at zero; the caller first pays any
   * milestone of the old week that was reached but not claimed (`owedMilestones`), so nothing reached is lost.
   */
  addTokens(weekKey: number, n: number): void {
    if (this.week.key !== weekKey) this.week = { key: weekKey, tokens: 0, claimed: [] };
    this.week.tokens += n;
  }

  /** Milestones (their token counts) reached and not claimed in the week the state holds. */
  owedMilestones(page: OfferPageDef): number[] {
    return page.track.filter((m) => m.tokens <= this.week.tokens && !this.week.claimed.includes(m.tokens)).map((m) => m.tokens);
  }

  /** Marks a milestone claimed (the caller pays it once, ref `track:<week>:<tokens>`). */
  claimMilestone(page: OfferPageDef, tokens: number): OfferItem[] | string {
    const m = page.track.find((x) => x.tokens === tokens);
    if (!m) return "no such milestone";
    if (this.week.tokens < tokens) return "not reached";
    if (this.week.claimed.includes(tokens)) return "already claimed";
    this.week.claimed.push(tokens);
    return [...m.items];
  }

  isGranted(transactionId: string): boolean { return this.granted.includes(transactionId); }
  markGranted(transactionId: string): void { if (!this.isGranted(transactionId)) this.granted.push(transactionId); }

  /** Records what was shown before the store is asked (the newest per product replaces nothing: FIFO per product). */
  pushIntent(i: DealIntent): void {
    this.intents = [...this.intents.filter((x) => i.at - x.at < INTENT_TTL_SECONDS), i].slice(-MAX_INTENTS);
  }

  /**
   * Drops an intent whose purchase ended without a transaction (cancelled, failed), so a later purchase of the same
   * product is granted as then shown, not as this one was. Matched by product and time. Returns whether one went.
   */
  dropIntent(i: Pick<DealIntent, "product" | "at">): boolean {
    const k = this.intents.findIndex((x) => x.product === i.product && x.at === i.at);
    if (k < 0) return false;
    this.intents.splice(k, 1);
    return true;
  }

  /** Takes the oldest live intent for a product (null: none, the caller grants the offer as it stands). */
  takeIntent(product: string, now: number): DealIntent | null {
    const k = this.intents.findIndex((x) => x.product === product && now - x.at < INTENT_TTL_SECONDS);
    if (k < 0) return null;
    const [i] = this.intents.splice(k, 1);
    return i;
  }

  toJson(): DealsJson {
    return {
      bought: copyBought(this.bought), picks: copyLists(this.picks), prizes: { ...this.prizes }, gearSlots: copyLists(this.gearSlots),
      daily: { ...this.daily }, week: { ...this.week, claimed: [...this.week.claimed] }, granted: [...this.granted], intents: this.intents.map((i) => ({ ...i, picks: [...i.picks], gearSlots: [...i.gearSlots] })),
      seen: [...this.seen],
    };
  }

  /** Lenient restore, field by field: the section holds paid state, so one bad entry never costs the rest. */
  static fromJson(j: unknown): DealsState {
    const s = new DealsState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<DealsJson>;
    for (const [k, b] of Object.entries(src.bought ?? {})) if (b && isInt(b.key) && isInt(b.n) && b.n > 0) s.bought[k] = { key: b.key, n: b.n };
    for (const [k, v] of Object.entries(src.picks ?? {})) s.picks[k] = choiceList(v);
    for (const [k, v] of Object.entries(src.prizes ?? {})) if (isInt(v)) s.prizes[k] = v;
    for (const [k, v] of Object.entries(src.gearSlots ?? {})) s.gearSlots[k] = choiceList(v);
    const d = src.daily;
    if (d && isInt(d.day) && typeof d.theme === "string" && isInt(d.changes)) s.daily = { day: d.day, theme: d.theme, changes: d.changes };
    const w = src.week;
    if (w && isInt(w.key) && isInt(w.tokens)) s.week = { key: w.key, tokens: Math.max(0, w.tokens), claimed: intList(w.claimed) };
    if (Array.isArray(src.granted)) s.granted = src.granted.filter((t): t is string => typeof t === "string" && t !== "");
    if (Array.isArray(src.intents)) s.intents = src.intents.filter(validIntent).slice(-MAX_INTENTS);
    if (Array.isArray(src.seen)) s.seen = src.seen.filter((p): p is string => typeof p === "string" && p !== "");
    return s;
  }
}

/** A choice list by position: anything not a whole number reads as unchosen (−1), so later choices keep their slot. */
function choiceList(v: unknown): number[] { return Array.isArray(v) ? v.map((x) => (isInt(x) ? x : NO_CHOICE)) : []; }

/** Sets position `i` of a record's list, filling any gap with −1 (never a sparse array: it would not survive JSON). */
function setAt(r: Record<string, number[]>, key: string, i: number, value: number): void {
  const list = [...(r[key] ?? [])];
  while (list.length < i) list.push(NO_CHOICE);
  list[i] = value;
  r[key] = list;
}

function copyLists(r: Record<string, number[]>): Record<string, number[]> {
  return Object.fromEntries(Object.entries(r).map(([k, v]) => [k, [...v]]));
}
function copyBought(r: Record<string, { key: number; n: number }>): Record<string, { key: number; n: number }> {
  return Object.fromEntries(Object.entries(r).map(([k, v]) => [k, { ...v }]));
}
function validIntent(i: unknown): i is DealIntent {
  const x = i as DealIntent;
  return typeof x === "object" && x !== null && typeof x.product === "string" && typeof x.offerId === "string" && typeof x.pageId === "string"
    && isInt(x.periodKey) && isInt(x.priceCents) && typeof x.theme === "string" && Array.isArray(x.picks) && x.picks.every(isInt) && isInt(x.prize)
    && Array.isArray(x.gearSlots) && x.gearSlots.every(isInt) && Number.isFinite(x.at);
}
